// Modul Hlídací pes — admin stránka s metrikami a výpisem záznamů.
// Hlavní cíl: ukázat businessový přínos modulu — kolik watchdogů konvertovalo
// na objednávku a jaký % z měsíčního obratu to představuje. Info box dole
// popisuje metodiku, ať uživatel vždy ví, jak se čísla počítají.

import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import type { MeResponse } from '@sklad-sync/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Props = { user: MeResponse };

type Metrics = {
  from: string;
  to: string;
  signups: number;
  returned: number;
  bought: number;
  attributed_revenue: number;
  total_revenue: number;
  attribution_pct: number;
  attribution_window_days: number;
};

type TopProduct = {
  product_code: string;
  product_name: string | null;
  image_url: string | null;
  product_url: string | null;
  signups: number;
};

type FunnelDetailRow = {
  watchdog_id: number;
  watchdog_created_at: number;
  watchdog_product_name: string;
  watchdog_variant_label: string;
  watchdog_product_code: string | null;
  watchdog_image_url: string | null;
  watchdog_product_url: string | null;
  customer_email: string;
  order_code: string;
  order_creation_time: string;
  order_total: number; // v CZK (přepočítané)
  order_total_original: number; // v originální měně
  currency_code: string | null;
  matched_item_revenue: number | null; // v CZK
  matched_item_revenue_original: number | null; // v originální měně
  matched_item_quantity: number | null;
};

type ListRow = {
  id: number;
  created_at: number;
  product_name: string;
  variant_label: string;
  product_code: string | null;
  product_url: string | null;
  image_url: string | null;
  customer_email: string;
  restocked_at: number | null;
  converted: boolean;
};

const PAGE_SIZE = 50;
// Kolik předchozích měsíců nabídnout jako rychlá tlačítka (current + N-1).
const MONTH_PRESETS_COUNT = 6;

function fmtMoney(n: number): string {
  return `${n.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDate(year: number, month0: number, day: number): string {
  return `${year}-${pad2(month0 + 1)}-${pad2(day)}`;
}

/**
 * Vrátí seznam preset měsíců v chronologickém pořadí (nejstarší vlevo,
 * aktuální měsíc vpravo) — ať se v UI vykreslí intuitivně zleva doprava.
 * Každý preset má from = 1. den, to = poslední den měsíce (lokálně CET).
 */
function listMonthPresets(count: number): { label: string; from: string; to: string }[] {
  const out: { label: string; from: string; to: string }[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(currentYear, now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth(); // 0-indexed
    const lastDay = new Date(y, m + 1, 0).getDate();
    // Label: "Květen" pro aktuální rok, jinak "Květen 2025"
    const monthName = d.toLocaleDateString('cs-CZ', { month: 'long' });
    const label = y === currentYear ? monthName : `${monthName} ${y}`;
    out.push({
      label: label.charAt(0).toUpperCase() + label.slice(1),
      from: isoDate(y, m, 1),
      to: isoDate(y, m, lastDay),
    });
  }
  return out;
}

/**
 * Vytvoří lidský label pro zvolený rozsah. Pokud kryje celý 1 měsíc, vrátí
 * jeho jméno; jinak "1. 5. 2026 – 31. 5. 2026".
 */
function rangeLabel(from: string, to: string): string {
  const fm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(from);
  const tm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(to);
  if (fm && tm) {
    const fy = Number(fm[1]);
    const fmo = Number(fm[2]);
    const fd = Number(fm[3]);
    const ty = Number(tm[1]);
    const tmo = Number(tm[2]);
    const td = Number(tm[3]);
    const lastDay = new Date(fy, fmo, 0).getDate();
    if (fy === ty && fmo === tmo && fd === 1 && td === lastDay) {
      const d = new Date(fy, fmo - 1, 1);
      const monthName = d.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
      return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    }
  }
  const fmt = (s: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return s;
    return `${Number(m[3])}. ${Number(m[2])}. ${m[1]}`;
  };
  return `${fmt(from)} – ${fmt(to)}`;
}

export function WatchdogPage(_props: Props) {
  const presets = useMemo(() => listMonthPresets(MONTH_PRESETS_COUNT), []);
  const defaultPreset = presets[presets.length - 1]!;
  const [from, setFrom] = useState<string>(defaultPreset.from);
  const [to, setTo] = useState<string>(defaultPreset.to);
  const [customMode, setCustomMode] = useState(false);
  const [productCode, setProductCode] = useState('');
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [rows, setRows] = useState<ListRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectStatus, setDetectStatus] = useState<string | null>(null);
  const [modalStep, setModalStep] = useState<'returned' | 'bought' | null>(null);

  // Reset paginace při změně filtru.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), [from, to, productCode]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ from, to });
      const trimmed = productCode.trim();
      if (trimmed) params.set('product_code', trimmed);

      const topParams = new URLSearchParams({ from, to });

      const listParams = new URLSearchParams(params);
      listParams.set('page', String(page));
      listParams.set('page_size', String(PAGE_SIZE));

      const [m, t, l] = await Promise.all([
        apiFetch<Metrics>(`/api/watchdog/admin/metrics?${params.toString()}`),
        apiFetch<{ products: TopProduct[] }>(
          `/api/watchdog/admin/top-products?${topParams.toString()}`,
        ),
        apiFetch<{ rows: ListRow[]; total: number }>(
          `/api/watchdog/admin/list?${listParams.toString()}`,
        ),
      ]);
      setMetrics(m);
      setTopProducts(t.products ?? []);
      setRows(l.rows ?? []);
      setTotalRows(l.total ?? 0);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.describe() : 'Chyba načítání');
    } finally {
      setLoading(false);
    }
  }, [from, to, productCode, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Hlídací pes</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Notifikace zákaznic o vyprodaných variantách + měření přínosu modulu pro V-Mart.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            placeholder="SKU filtr (např. K1083/110)"
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            className="text-sm bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary w-56"
          />
          <button
            type="button"
            onClick={async () => {
              setDetectStatus('Sync objednávek + detekce…');
              try {
                const r = await apiFetch<{
                  checked: number;
                  restocked: number;
                  ordersSync?: { synced: number; error?: string };
                }>('/api/watchdog/admin/detect-restocks', { method: 'POST' });
                const parts = [
                  `objednávek syncnutých: ${r.ordersSync?.synced ?? 0}`,
                  `watchdogů zkontrolováno: ${r.checked}`,
                  `naskladněno: ${r.restocked}`,
                ];
                setDetectStatus(parts.join(' · '));
                await load();
              } catch (e: unknown) {
                setDetectStatus(e instanceof ApiError ? e.describe() : 'Chyba detekce');
              }
            }}
            className="text-sm bg-bg border border-border rounded-lg px-3 py-2 hover:bg-surface-hover cursor-pointer"
            title="Stáhne posledních 2h objednávek ze Shoptetu + detekuje restock pro watchdogy v okně 72h"
          >
            Aktualizovat data
          </button>
        </div>
      </div>

      {/* Filtrace období */}
      <div className="bg-surface border border-border rounded-xl p-3 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-text-secondary mr-1">Období:</span>
        {presets.map((p) => {
          const isActive = !customMode && from === p.from && to === p.to;
          return (
            <button
              key={`${p.from}-${p.to}`}
              type="button"
              onClick={() => {
                setFrom(p.from);
                setTo(p.to);
                setCustomMode(false);
              }}
              className={`text-sm px-3 py-1.5 rounded-lg border transition-colors cursor-pointer ${
                isActive
                  ? 'bg-primary text-white border-primary'
                  : 'bg-bg border-border hover:bg-surface-hover'
              }`}
            >
              {p.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setCustomMode(true)}
          className={`text-sm px-3 py-1.5 rounded-lg border transition-colors cursor-pointer ${
            customMode
              ? 'bg-primary text-white border-primary'
              : 'bg-bg border-border hover:bg-surface-hover'
          }`}
        >
          Vlastní rozsah
        </button>
        {customMode && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="text-sm bg-bg border border-border rounded-lg px-3 py-1.5 outline-none focus:border-primary"
            />
            <span className="text-text-muted">–</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="text-sm bg-bg border border-border rounded-lg px-3 py-1.5 outline-none focus:border-primary"
            />
          </div>
        )}
      </div>
      {detectStatus && (
        <div className="text-xs text-text-secondary bg-surface border border-border rounded-lg px-3 py-2">
          {detectStatus}
        </div>
      )}

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Headline KPI */}
      {metrics && (
        <div className="bg-gradient-to-r from-primary-50 to-surface rounded-xl p-6 shadow-card">
          <div className="text-sm text-text-secondary mb-1">
            Attribuovaný obrat za {rangeLabel(from, to)}
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-4xl font-bold text-primary">
              {fmtMoney(metrics.attributed_revenue)}
            </div>
            <div className="text-base text-text">
              = <strong className="text-text">{metrics.attribution_pct.toFixed(2)} %</strong>{' '}
              měsíčního obratu ({fmtMoney(metrics.total_revenue)})
            </div>
          </div>
          <div className="text-xs text-text-muted mt-2">
            Tight match: stejný email + stejná varianta + {metrics.attribution_window_days} dní od
            signupu (non-cancelled). Viz vysvětlení dole.
          </div>
        </div>
      )}

      {/* Funnel */}
      {metrics && (
        <div className="bg-surface rounded-xl p-5 shadow-card">
          <div className="text-sm font-semibold text-text mb-3">Funnel</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FunnelStep
              label="Hlídači"
              value={metrics.signups}
              tooltip={
                <>
                  <p className="font-semibold mb-1">Hlídači</p>
                  <p>
                    Počet úspěšných signupů hlídacího psa v měsíci (jen ty, kterým se podařilo
                    odeslat notifikační email).
                  </p>
                  <p className="mt-2 text-text-muted">
                    Přesnost: <strong className="text-text">100 %</strong> — fact z naší DB.
                  </p>
                </>
              }
            />
            <FunnelStep
              label="Vrátila se"
              value={metrics.returned}
              of={metrics.signups}
              onClick={metrics.returned > 0 ? () => setModalStep('returned') : undefined}
              tooltip={
                <>
                  <p className="font-semibold mb-1">Vrátila se</p>
                  <p>
                    Počet hlídačů, kde zákaznice (stejný e-mail) vytvořila{' '}
                    <strong>jakoukoliv</strong> objednávku do {metrics.attribution_window_days} dní
                    od signupu (non-cancelled). Tj. vrátila se do shopu, ale{' '}
                    <em>ne nutně koupila sledovanou velikost</em> — mohla koupit cokoliv jiného.
                  </p>
                  <p className="mt-2 text-text-muted">
                    Přesnost: <strong className="text-text">měkký signál</strong>.
                    <br />✓ Indikuje návrat zákaznice
                    <br />✗ Může být koincidence (koupila by stejně)
                    <br />✗ Pokud checkout pod jiným e-mailem (guest), missed
                  </p>
                  <p className="mt-2 text-text-muted">
                    Pro tvrdou atribuci přínosu modulu používej <strong>Koupila</strong> (stejná
                    SKU).
                  </p>
                </>
              }
            />
            <FunnelStep
              label="Koupila"
              value={metrics.bought}
              of={metrics.signups}
              onClick={metrics.bought > 0 ? () => setModalStep('bought') : undefined}
              tooltip={
                <>
                  <p className="font-semibold mb-1">Koupila (tight match)</p>
                  <p>
                    Stejný e-mail <strong>+ stejná SKU</strong> + do{' '}
                    {metrics.attribution_window_days} dní od signupu + non-cancelled. Hlavní KPI
                    přínosu modulu.
                  </p>
                  <p className="mt-2 text-text-muted">
                    Přesnost: <strong className="text-text">konzervativní spodní hranice</strong>.
                    Část zákaznic by koupila i bez emailu od Shoptetu (false positive), ale tight
                    match eliminuje většinu náhod. Reálný impact je nejspíš vyšší, ne nižší.
                  </p>
                </>
              }
            />
            <FunnelStep
              label="Obrat"
              value={fmtMoney(metrics.attributed_revenue)}
              onClick={metrics.attributed_revenue > 0 ? () => setModalStep('bought') : undefined}
              tooltip={
                <>
                  <p className="font-semibold mb-1">Atribuovaný obrat</p>
                  <p>
                    Suma <code className="bg-bg/20 px-1 rounded">total_price_with_vat</code> položek
                    objednávek, které spadají do <strong>Koupila</strong> (= jen položky se
                    sledovanou SKU, ne celá objednávka).
                  </p>
                </>
              }
            />
          </div>
        </div>
      )}

      {/* Top 10 nejhlídanějších produktů */}
      {topProducts.length > 0 && (
        <div className="bg-surface rounded-xl shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <div className="text-sm font-semibold text-text">Top 10 nejhlídanějších produktů</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-text-secondary border-b border-border">
                <tr>
                  <th className="text-left font-semibold px-4 py-2 w-16"></th>
                  <th className="text-left font-semibold px-4 py-2 w-40">SKU</th>
                  <th className="text-left font-semibold px-4 py-2">Produkt</th>
                  <th className="text-right font-semibold px-4 py-2 w-24">Hlídači</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((p) => (
                  <tr key={p.product_code} className="border-b border-border last:border-0">
                    <td className="px-2 py-2">
                      {p.image_url ? (
                        p.product_url ? (
                          <a href={p.product_url} target="_blank" rel="noreferrer">
                            <img
                              src={p.image_url}
                              alt=""
                              className="w-10 h-10 rounded object-cover border border-border"
                              loading="lazy"
                            />
                          </a>
                        ) : (
                          <img
                            src={p.image_url}
                            alt=""
                            className="w-10 h-10 rounded object-cover border border-border"
                            loading="lazy"
                          />
                        )
                      ) : null}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{p.product_code}</td>
                    <td className="px-4 py-2">{p.product_name ?? '—'}</td>
                    <td className="px-4 py-2 text-right font-semibold">{p.signups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Seznam záznamů */}
      <div className="bg-surface rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="text-sm font-semibold text-text">Záznamy ({totalRows})</div>
          <div className="text-xs text-text-secondary">
            Strana {page} / {totalPages}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg text-text-secondary border-b border-border">
              <tr>
                <th className="text-left font-semibold px-4 py-2 w-40">Kdy</th>
                <th className="text-left font-semibold px-2 py-2 w-16"></th>
                <th className="text-left font-semibold px-4 py-2">Produkt</th>
                <th className="text-left font-semibold px-4 py-2 w-24">Velikost</th>
                <th className="text-left font-semibold px-4 py-2">E-mail</th>
                <th className="text-left font-semibold px-4 py-2 w-32">Stav</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center text-text-muted py-6">
                    Načítám…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-text-muted py-6">
                    Žádné záznamy v tomto měsíci.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-bg/50">
                  <td className="px-4 py-2 text-xs text-text-muted whitespace-nowrap">
                    {fmtDateTime(r.created_at)}
                  </td>
                  <td className="px-2 py-2">
                    {r.image_url ? (
                      r.product_url ? (
                        <a href={r.product_url} target="_blank" rel="noreferrer">
                          <img
                            src={r.image_url}
                            alt=""
                            className="w-10 h-10 rounded object-cover border border-border"
                            loading="lazy"
                          />
                        </a>
                      ) : (
                        <img
                          src={r.image_url}
                          alt=""
                          className="w-10 h-10 rounded object-cover border border-border"
                          loading="lazy"
                        />
                      )
                    ) : null}
                  </td>
                  <td className="px-4 py-2">
                    {r.product_url ? (
                      <a
                        href={r.product_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {r.product_name}
                      </a>
                    ) : (
                      r.product_name
                    )}
                    {r.product_code && (
                      <span className="ml-2 text-xs text-text-muted font-mono">
                        {r.product_code}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-semibold">{r.variant_label}</td>
                  <td className="px-4 py-2 text-xs">
                    <a href={`mailto:${r.customer_email}`} className="text-primary hover:underline">
                      {r.customer_email}
                    </a>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.converted ? (
                      <span className="bg-success-light text-success px-2 py-0.5 rounded font-semibold">
                        ✓ Koupila
                      </span>
                    ) : r.restocked_at ? (
                      <span className="bg-warning-light text-warning px-2 py-0.5 rounded">
                        Naskladněno do 72 h
                      </span>
                    ) : (
                      <span className="text-text-muted">Čeká</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-border flex justify-between">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="text-sm px-3 py-1 border border-border rounded disabled:opacity-30 hover:bg-bg"
            >
              ← Předchozí
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="text-sm px-3 py-1 border border-border rounded disabled:opacity-30 hover:bg-bg"
            >
              Další →
            </button>
          </div>
        )}
      </div>

      {/* Info box — metodika */}
      <div className="bg-bg border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-text mb-3">
          🧠 Jak měření a vyhodnocování funguje
        </h3>
        <div className="text-sm text-text-secondary space-y-3 max-w-3xl">
          <p>
            <strong className="text-text">1) Sběr dat:</strong> Když zákaznice na e-shopu klikne na
            „Aktivujte si hlídacího psa" u vyprodané velikosti, zachytíme to (kromě standardního
            Shoptet hlídače) — uložíme čas, produkt, variantu (SKU jako{' '}
            <code className="text-xs bg-surface px-1 rounded">K1083/110</code>) a e-mail zákaznice.
            Současně cinkne notifikační email na <em>zakaznici@v-mart.cz</em>, ať můžeš reagovat
            osobně.
          </p>
          <p>
            <strong className="text-text">2) Detekce naskladnění (do 72 h):</strong> Při signupu si
            uložíme stav varianty z Shoptet API (
            <code className="text-xs bg-surface px-1 rounded">signup_quantity</code>). Pak pro
            otevřené hlídače v okně 72 h sledujeme tranzici{' '}
            <code className="text-xs bg-surface px-1 rounded">0 → &gt; 0</code> — když varianta
            získá sklad, označíme „Naskladněno do 72 h". Bez at-signup snapshotu (chybí v API)
            detekce ten záznam nedotkne → žádné false positives. Detekce běží auto při stocks syncu
            + lze ji spustit ručně tlačítkem nahoře.
          </p>
          <p>
            <strong className="text-text">3) Atribuce koupě (tight match):</strong> Když najdeme
            objednávku se <strong>stejným e-mailem</strong> + <strong>stejnou SKU</strong> +{' '}
            <strong>do {metrics?.attribution_window_days ?? 30} dní od signupu</strong> + není
            stornovaná → tu objednávku přiřadíme watchdogu. Sčítáme{' '}
            <code className="text-xs bg-surface px-1 rounded">total_price_with_vat</code> položek
            odpovídajících sledované SKU.
          </p>
          <p>
            <strong className="text-text">4) „% z měsíčního obratu":</strong> Attribuovaný obrat /
            celkový obrat všech nestornovaných objednávek v měsíci × 100.
          </p>
          <p className="text-xs text-text-muted border-t border-border pt-3 mt-3 leading-relaxed">
            <strong>Pozor — interpretace:</strong> Je to <em>korelační</em> atribuce, ne kauzální.
            Část zákaznic by koupila i bez emailu od Shoptetu. Tight match (stejná SKU + krátké
            okno) ale vyloučí většinu falešných korelací, takže výsledná čísla jsou{' '}
            <strong className="text-text">konzervativní spodní hranice</strong> přínosu modulu.
            Reálný impact je nejspíš o něco vyšší. Když dashboard ukazuje „watchdog vydělal 5 %",
            reálně to bude spíš víc, ne míň.
          </p>
        </div>
      </div>

      {modalStep && (
        <FunnelDetailModal
          step={modalStep}
          from={from}
          to={to}
          productCode={productCode.trim() || null}
          onClose={() => setModalStep(null)}
        />
      )}
    </div>
  );
}

function FunnelStep({
  label,
  value,
  of,
  tooltip,
  onClick,
}: {
  label: string;
  value: number | string;
  of?: number;
  tooltip?: React.ReactNode;
  onClick?: () => void;
}) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={`bg-bg rounded-lg p-3 text-center border transition-colors ${
        clickable
          ? 'border-border cursor-pointer hover:bg-surface-hover hover:border-primary'
          : 'border-border'
      }`}
    >
      <div className="text-xs text-text-secondary flex items-center justify-center gap-1.5">
        <span>{label}</span>
        {tooltip && (
          <span className="relative group cursor-help inline-flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-text-muted hover:text-text"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span
              className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity
                         absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20
                         w-72 max-w-[18rem] bg-surface border border-border shadow-lg
                         rounded-lg p-3 text-xs text-text text-left leading-relaxed normal-case font-normal
                         whitespace-normal pointer-events-none"
              role="tooltip"
            >
              {tooltip}
            </span>
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-text mt-1">{value}</div>
      {typeof value === 'number' && of !== undefined && of > 0 && (
        <div className="text-xs text-text-muted mt-0.5">{((value / of) * 100).toFixed(1)} %</div>
      )}
    </div>
  );
}

/**
 * Modal s detailem funnel kroku — vypisuje konkrétní (watchdog, objednávka)
 * páry. Vrátila se: jakákoliv objednávka zákaznice. Koupila/Obrat: jen položky
 * matching sledovanou SKU + součet vpravo dole.
 */
function FunnelDetailModal({
  step,
  from,
  to,
  productCode,
  onClose,
}: {
  step: 'returned' | 'bought';
  from: string;
  to: string;
  productCode: string | null;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<FunnelDetailRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ step, from, to });
    if (productCode) params.set('product_code', productCode);
    apiFetch<{ rows: FunnelDetailRow[] }>(`/api/watchdog/admin/funnel-detail?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setRows(r.rows ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.describe() : 'Chyba načítání');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, from, to, productCode]);

  // ESC zavře modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = step === 'returned' ? 'Vrátila se — návratové objednávky' : 'Koupila — konverze';

  const totalRevenue = rows?.reduce((sum, r) => sum + (r.matched_item_revenue ?? 0), 0) ?? 0;
  const totalOrders = rows?.length ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-surface rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-text">{title}</h2>
            {!loading && !error && (
              <p className="text-xs text-text-muted mt-0.5">
                {totalOrders}{' '}
                {totalOrders === 1 ? 'položka' : totalOrders < 5 ? 'položky' : 'položek'}
                {step === 'bought' && totalRevenue > 0 && (
                  <>
                    {' · '}celkem <strong className="text-text">{fmtMoney(totalRevenue)}</strong>
                  </>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text text-2xl leading-none px-2 cursor-pointer"
            aria-label="Zavřít"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading && <div className="text-center text-text-muted py-12">Načítám…</div>}
          {error && (
            <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 m-6">
              {error}
            </div>
          )}
          {!loading && !error && rows && rows.length === 0 && (
            <div className="text-center text-text-muted py-12">Žádné položky.</div>
          )}
          {!loading && !error && rows && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-bg text-text-secondary border-b border-border sticky top-0">
                  <tr>
                    <th className="text-left font-semibold px-4 py-2 w-32">Signup</th>
                    <th className="text-left font-semibold px-2 py-2 w-14"></th>
                    <th className="text-left font-semibold px-4 py-2">Hlídaný produkt</th>
                    <th className="text-left font-semibold px-4 py-2">E-mail</th>
                    <th className="text-left font-semibold px-4 py-2 w-44">Objednávka</th>
                    <th className="text-right font-semibold px-4 py-2 w-28">
                      {step === 'bought' ? 'Položka' : 'Obj. celkem'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr
                      key={`${r.watchdog_id}-${r.order_code}-${idx}`}
                      className="border-b border-border last:border-0 hover:bg-bg/50"
                    >
                      <td className="px-4 py-2 text-xs text-text-muted whitespace-nowrap">
                        {fmtDateTime(r.watchdog_created_at)}
                      </td>
                      <td className="px-2 py-2">
                        {r.watchdog_image_url ? (
                          r.watchdog_product_url ? (
                            <a href={r.watchdog_product_url} target="_blank" rel="noreferrer">
                              <img
                                src={r.watchdog_image_url}
                                alt=""
                                className="w-10 h-10 rounded object-cover border border-border"
                                loading="lazy"
                              />
                            </a>
                          ) : (
                            <img
                              src={r.watchdog_image_url}
                              alt=""
                              className="w-10 h-10 rounded object-cover border border-border"
                              loading="lazy"
                            />
                          )
                        ) : null}
                      </td>
                      <td className="px-4 py-2">
                        <div>
                          {r.watchdog_product_url ? (
                            <a
                              href={r.watchdog_product_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline"
                            >
                              {r.watchdog_product_name}
                            </a>
                          ) : (
                            r.watchdog_product_name
                          )}
                        </div>
                        <div className="text-xs text-text-muted">
                          {r.watchdog_product_code && (
                            <span className="font-mono">{r.watchdog_product_code}</span>
                          )}
                          {r.watchdog_variant_label && (
                            <span className="ml-2">vel. {r.watchdog_variant_label}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <a
                          href={`mailto:${r.customer_email}`}
                          className="text-primary hover:underline"
                        >
                          {r.customer_email}
                        </a>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <div className="font-mono">#{r.order_code}</div>
                        <div className="text-text-muted">
                          {fmtDateTime(parseShoptetTime(r.order_creation_time))}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold whitespace-nowrap">
                        <div>
                          {step === 'bought'
                            ? fmtMoney(r.matched_item_revenue ?? 0)
                            : fmtMoney(r.order_total ?? 0)}
                        </div>
                        {r.currency_code && r.currency_code !== 'CZK' && (
                          <div className="text-xs text-text-muted font-normal">
                            ={' '}
                            {step === 'bought'
                              ? r.matched_item_revenue_original
                              : r.order_total_original}{' '}
                            {r.currency_code}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Shoptet creation_time "2026-05-30T13:18:28+0200" → unix ms (treat as local CET).
// Pro UI stačí 1:1 reprezentace — drobný 2h posun při strip-TZ nevadí.
function parseShoptetTime(s: string): number {
  // Nahradíme "+0200" za "+02:00" pro Date parser.
  if (s.length >= 24 && (s[19] === '+' || s[19] === '-')) {
    const fixed = `${s.slice(0, 22)}:${s.slice(22)}`;
    const ms = Date.parse(fixed);
    if (Number.isFinite(ms)) return ms;
  }
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : Date.now();
}
